import type { CSSProperties } from "react";
import type { Rect } from "react-tiny-popover";
import { RADIUS, SHADOW, SURFACE } from "@/theme/tokens";

/**
 * Geometry for the Punch Action Center popover, shared by the popover shell (`DayViewItem`) and the
 * panel itself (`PunchActionCenter`) so the two can't drift apart — the panel's height cap only
 * guarantees the popover fits on screen while it agrees with the inset the shell repositions against.
 *
 * The panel used to be sized to the day card it opens from (`contentRootStyle.width = cardWidthPx`),
 * which is whatever the 7-day grid hands one column: 125px at a 896px window, 203px at 1440px. Every
 * label truncated at laptop widths and the bulk action was clipped by the shell's `overflow: hidden`.
 * It is a floating panel over the grid, so it sizes itself now — the column width is not a constraint
 * on it.
 */

/** Panel width — wide enough for a name plus its labelled hours breakdown on one line. */
export const ACTION_CENTER_WIDTH_PX = 340;

/**
 * Minimum gap kept between the panel and the viewport edges.
 *
 * `react-tiny-popover` nudges the popover back inside its boundary (the `<body>` box, which is
 * `height: 100%` — i.e. the viewport) minus this inset, but ONLY while at least one candidate
 * position doesn't violate the boundary on its own axis. `position: "right"`/`"left"` are checked
 * horizontally only, so a panel that fits beside the column is always nudged vertically into view —
 * which is what the old `["bottom", "top"]` pair could not do: on a short window both violated
 * vertically, the library gave up repositioning, and the panel rendered below the fold where nothing
 * could scroll it into view (APP-90).
 */
export const ACTION_CENTER_VIEWPORT_INSET_PX = 16;

/** Gap between the day column and the panel floating beside it. */
export const ACTION_CENTER_ANCHOR_GAP_PX = 8;

/**
 * Tallest the panel gets: enough for its header, six rows at natural height and the footer, so the
 * common case never opens with an inner scrollbar. Longer lists scroll inside the list region only —
 * the header and the bulk action stay put.
 */
export const ACTION_CENTER_MAX_HEIGHT_PX = 480;

/**
 * The panel's height cap. Bounded by the viewport as well as by `ACTION_CENTER_MAX_HEIGHT_PX`, so on
 * a short window the panel is never taller than the space the popover can be nudged into — the
 * invariant that keeps it fully on screen (see `ACTION_CENTER_VIEWPORT_INSET_PX`).
 */
export const ACTION_CENTER_MAX_HEIGHT = `min(${ACTION_CENTER_MAX_HEIGHT_PX}px, calc(100vh - ${2 * ACTION_CENTER_VIEWPORT_INSET_PX}px))`;

/**
 * The popover shell around the panel. `overflow: hidden` stays: it clips the panel's category-colored
 * top flag to the rounded corner, and nothing needs to escape the shell now that the panel sizes
 * itself and scrolls internally.
 */
export const actionCenterPopoverRootStyle: CSSProperties = {
	width: ACTION_CENTER_WIDTH_PX,
	maxWidth: `calc(100vw - ${2 * ACTION_CENTER_VIEWPORT_INSET_PX}px)`,
	boxSizing: "border-box",
	padding: 0,
	borderRadius: RADIUS.CARD,
	background: SURFACE,
	boxShadow: SHADOW.POPOVER_FLOAT,
	overflow: "hidden",
} as const satisfies CSSProperties;

/**
 * Vertical offset that lines the panel up with the Punch Summary row it was opened from.
 *
 * The popover is anchored to the whole day card (that wrapper is the grid's flex item, so it can't
 * move), and `align: "start"` therefore puts the panel at the *top* of the card — up to ~250px above
 * the row that was actually clicked, which reads as an unrelated floating panel. This shifts it back
 * down onto its row, clamped to the room that's left so the alignment can never undo the
 * keep-it-on-screen guarantee: the popover's own repositioning runs first, and this only spends
 * whatever space remains between the panel and the viewport edges.
 */
export const actionCenterRowAlignOffset = (popoverRect: Rect, anchorRow: HTMLElement | null): number => {
	if (!anchorRow) return 0;
	const wanted = anchorRow.getBoundingClientRect().top - popoverRect.top;
	const roomBelow = window.innerHeight - ACTION_CENTER_VIEWPORT_INSET_PX - popoverRect.bottom;
	const roomAbove = popoverRect.top - ACTION_CENTER_VIEWPORT_INSET_PX;
	return Math.max(-roomAbove, Math.min(wanted, roomBelow));
};
